import { useEffect, useMemo, useRef, useState } from "react";
import {
    DndContext,
    PointerSensor,
    TouchSensor,
    useSensor,
    useSensors,
    closestCenter,
} from "@dnd-kit/core";
import {
    SortableContext,
    useSortable,
    arrayMove,
    verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { groupItemsByCategory, UNCATEGORISED_LABEL } from "@/lib/menuGrouping";
import { adminUpdateBrand, adminReorderItems } from "@/lib/api";
import { formatMoney } from "@/lib/orderUtils";

const dragHandleCls =
    "cursor-grab active:cursor-grabbing select-none text-muted hover:text-gold font-mono text-[18px] grid place-items-center w-10 h-10 touch-none";

const SortableCategorySection = ({ id, category, items, description, descEditable, onDescChange, onEdit, onDuplicate, onMove, canMoveUp, canMoveDown }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className="mb-8 border border-transparent"
            data-testid={`admin-cat-${category}`}
        >
            <div className="mb-3 pb-2 border-b border-[var(--border)]">
                <div className="flex items-baseline justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                        <span
                            {...attributes}
                            {...listeners}
                            className={dragHandleCls + " border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface-elevated)] shrink-0"}
                            title="Drag to reorder category"
                            data-testid={`cat-handle-${category}`}
                        >
                            ⠿
                        </span>
                        <h3
                            className="font-display text-[20px] truncate"
                            style={{
                                color: category === UNCATEGORISED_LABEL ? "var(--muted)" : "var(--gold)",
                            }}
                        >
                            {category}
                        </h3>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {/* One-tap move buttons — no drag needed, quickest way to
                            shuffle a category to top/bottom of a big menu. */}
                        {onMove && category !== UNCATEGORISED_LABEL && (
                            <div className="flex items-center gap-0.5" data-testid={`cat-move-${category}`}>
                                <CatMoveBtn onClick={() => onMove(category, "top")} disabled={!canMoveUp} title="Move to top" testid={`cat-top-${category}`}>⤒</CatMoveBtn>
                                <CatMoveBtn onClick={() => onMove(category, "up")} disabled={!canMoveUp} title="Move up" testid={`cat-up-${category}`}>▲</CatMoveBtn>
                                <CatMoveBtn onClick={() => onMove(category, "down")} disabled={!canMoveDown} title="Move down" testid={`cat-down-${category}`}>▼</CatMoveBtn>
                                <CatMoveBtn onClick={() => onMove(category, "bottom")} disabled={!canMoveDown} title="Move to bottom" testid={`cat-bottom-${category}`}>⤓</CatMoveBtn>
                            </div>
                        )}
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-muted whitespace-nowrap">
                            {items.length} {items.length === 1 ? "item" : "items"}
                        </span>
                    </div>
                </div>
                {descEditable && category !== UNCATEGORISED_LABEL ? (
                    <input
                        className="input mt-2 text-[12px] h-8 w-full bg-transparent"
                        value={description || ""}
                        onChange={(e) => onDescChange(category, e.target.value)}
                        placeholder="Short description — e.g. Smashed in cast-iron, never frozen"
                        data-testid={`cat-desc-input-${category}`}
                    />
                ) : description ? (
                    <p className="text-[12px] text-[var(--text)]/65 font-light leading-snug mt-1">{description}</p>
                ) : null}
            </div>
            <SortableItemsRow items={items} onEdit={onEdit} onDuplicate={onDuplicate} />
        </div>
    );
};

const CatMoveBtn = ({ onClick, disabled, title, testid, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        title={title}
        data-testid={testid}
        className="w-8 h-8 grid place-items-center border border-[var(--border)] font-mono text-[12px] text-muted hover:text-gold hover:border-gold disabled:opacity-25 disabled:pointer-events-none transition-colors"
    >
        {children}
    </button>
);

const SortableItemsRow = ({ items, onEdit, onDuplicate }) => {
    const ids = items.map((it) => it.id);
    return (
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
            <div
                className="grid gap-3"
                style={{ gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))" }}
            >
                {items.map((it) => (
                    <SortableItemCard key={it.id} id={it.id} item={it} onEdit={() => onEdit(it)} onDuplicate={onDuplicate} />
                ))}
            </div>
        </SortableContext>
    );
};

const SortableItemCard = ({ id, item, onEdit, onDuplicate }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };
    return (
        <div
            ref={setNodeRef}
            style={style}
            className="bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-all flex group"
            data-testid={`admin-item-${item.id}`}
        >
            <span
                {...attributes}
                {...listeners}
                className={`${dragHandleCls} border-r border-[var(--border)] shrink-0`}
                title="Drag to reorder item"
                data-testid={`item-handle-${item.id}`}
            >
                ⠿
            </span>
            <button
                onClick={onEdit}
                className="flex-1 text-left p-3 flex gap-3 items-center"
                data-testid={`admin-edit-${item.id}`}
            >
                {item.image_url && (
                    <img
                        src={item.image_url}
                        alt=""
                        className="w-14 h-14 object-cover shrink-0 bg-[var(--surface-elevated)]"
                    />
                )}
                <div className="flex-1 min-w-0">
                    <div className="font-display text-[15px] leading-tight truncate">
                        {item.name}
                        {item.popular && (
                            <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.18em] text-gold">
                                ★
                            </span>
                        )}
                        {item.available === false && (
                            <span className="ml-2 font-mono text-[9px] uppercase tracking-[0.18em] text-[#ff5246]">
                                86&apos;d
                            </span>
                        )}
                    </div>
                    <div className="font-mono text-[12px] text-muted mt-0.5">
                        {formatMoney(item.price)}
                    </div>
                </div>
            </button>
            {onDuplicate && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDuplicate(item); }}
                    className="border-l border-[var(--border)] px-3 font-mono text-[10px] uppercase tracking-[0.18em] text-muted opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity hover:text-gold shrink-0"
                    title="Duplicate this item (create a copy to edit)"
                    data-testid={`admin-duplicate-${item.id}`}
                >
                    ⧉ Copy
                </button>
            )}
        </div>
    );
};

/**
 * Top-level component for the admin menu list.
 * Allows drag-reordering of categories AND items within categories.
 * On drop, posts the new order to the backend and refreshes the parent brand.
 */
export const SortableMenuList = ({ brand, onEdit, onDuplicate, onRefresh }) => {
    // Maintain LOCAL state for immediate UI feedback while async patch runs.
    const initialGroups = useMemo(
        () => groupItemsByCategory(brand.items, brand.category_order || [], [], brand.category_descriptions || {}),
        [brand.items, brand.category_order, brand.category_descriptions]
    );
    const [groups, setGroups] = useState(initialGroups);
    const [busy, setBusy] = useState(false);
    const descTimerRef = useRef(null);

    // Re-sync local groups whenever the brand prop changes (after refresh).
    // We mirror props in state so optimistic UI works during a drag.
    useEffect(() => {
        setGroups(groupItemsByCategory(brand.items, brand.category_order || [], [], brand.category_descriptions || {}));
    }, [brand.items, brand.category_order, brand.category_descriptions]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
        // iPad / touch — small delay so a tap on the handle doesn't immediately fire scroll/click,
        // and a small tolerance so a finger jitter doesn't cancel the drag.
        useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } })
    );

    // Debounced save for category descriptions (saves 700ms after the user stops typing)
    const onDescChange = (cat, value) => {
        setGroups((gs) => gs.map((g) => (g.category === cat ? { ...g, description: value } : g)));
        if (descTimerRef.current) clearTimeout(descTimerRef.current);
        descTimerRef.current = setTimeout(() => {
            // Build the current dict from the latest groups
            setGroups((current) => {
                const next = {};
                for (const g of current) {
                    if (g.category !== UNCATEGORISED_LABEL && g.description) next[g.category] = g.description;
                }
                adminUpdateBrand(brand.id, { category_descriptions: next })
                    .then(() => onRefresh?.())
                    .catch((e) => console.error("desc save failed", e));
                return current;
            });
        }, 700);
    };

    const persistCategoryOrder = async (newOrder) => {
        setBusy(true);
        try {
            // strip "Other" from persisted order (it's a placeholder)
            const clean = newOrder.filter((c) => c !== UNCATEGORISED_LABEL);
            await adminUpdateBrand(brand.id, { category_order: clean });
            await onRefresh?.();
        } finally {
            setBusy(false);
        }
    };

    const persistItemOrder = async (flatItemIds) => {
        setBusy(true);
        try {
            await adminReorderItems(brand.id, flatItemIds);
            await onRefresh?.();
        } finally {
            setBusy(false);
        }
    };

    const onDragEnd = ({ active, over }) => {
        if (!over || active.id === over.id) return;
        const activeId = String(active.id);
        const overId = String(over.id);

        // Is this a category drag? category ids are prefixed "cat::"
        if (activeId.startsWith("cat::") && overId.startsWith("cat::")) {
            const oldIdx = groups.findIndex((g) => `cat::${g.category}` === activeId);
            const newIdx = groups.findIndex((g) => `cat::${g.category}` === overId);
            if (oldIdx < 0 || newIdx < 0) return;
            const reordered = arrayMove(groups, oldIdx, newIdx);
            setGroups(reordered);
            persistCategoryOrder(reordered.map((g) => g.category));
            return;
        }

        // Otherwise — item drag (within same category only for now; cross-category requires
        // changing item.category which is a separate edit. We keep it simple here.)
        const flat = groups.flatMap((g) => g.items);
        const fromIdx = flat.findIndex((it) => it.id === activeId);
        const toIdx = flat.findIndex((it) => it.id === overId);
        if (fromIdx < 0 || toIdx < 0) return;
        // Only allow same-category reorder
        if ((flat[fromIdx].category || "") !== (flat[toIdx].category || "")) return;

        const newGroups = groups.map((g) => {
            if (!g.items.some((it) => it.id === activeId)) return g;
            const oldI = g.items.findIndex((it) => it.id === activeId);
            const newI = g.items.findIndex((it) => it.id === overId);
            if (oldI < 0 || newI < 0) return g;
            return { ...g, items: arrayMove(g.items, oldI, newI) };
        });
        setGroups(newGroups);
        persistItemOrder(newGroups.flatMap((g) => g.items.map((it) => it.id)));
    };

    const onMoveCategory = (category, direction) => {
        // One-tap category reorder: up / down / top / bottom without needing
        // to drag. Filters "Other" out of the movable set — it's always last.
        const realCats = groups.filter((g) => g.category !== UNCATEGORISED_LABEL);
        const uncat = groups.find((g) => g.category === UNCATEGORISED_LABEL);
        const idx = realCats.findIndex((g) => g.category === category);
        if (idx < 0) return;
        let newIdx = idx;
        if (direction === "up") newIdx = Math.max(0, idx - 1);
        else if (direction === "down") newIdx = Math.min(realCats.length - 1, idx + 1);
        else if (direction === "top") newIdx = 0;
        else if (direction === "bottom") newIdx = realCats.length - 1;
        if (newIdx === idx) return;
        const reordered = arrayMove(realCats, idx, newIdx);
        // Re-append the "Other" bucket at the end so it stays put
        const nextGroups = uncat ? [...reordered, uncat] : reordered;
        setGroups(nextGroups);
        persistCategoryOrder(nextGroups.map((g) => g.category));
    };

    if (!groups.length) return null;
    const categoryIds = groups.map((g) => `cat::${g.category}`);
    const realCatCount = groups.filter((g) => g.category !== UNCATEGORISED_LABEL).length;

    return (
        <div className={busy ? "opacity-90" : ""} data-testid="admin-items-grid">
            {busy && (
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-3">
                    ⟲ Saving order…
                </div>
            )}
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
                    {groups.map((g, i) => {
                        // Position among real (movable) categories only — "Other" doesn't count.
                        const realIdx = groups.slice(0, i).filter((x) => x.category !== UNCATEGORISED_LABEL).length;
                        return (
                            <SortableCategorySection
                                key={g.category}
                                id={`cat::${g.category}`}
                                category={g.category}
                                items={g.items}
                                description={g.description || ""}
                                descEditable={true}
                                onDescChange={onDescChange}
                                onEdit={onEdit}
                                onDuplicate={onDuplicate}
                                onMove={onMoveCategory}
                                canMoveUp={realIdx > 0}
                                canMoveDown={realIdx < realCatCount - 1}
                            />
                        );
                    })}
                </SortableContext>
            </DndContext>
        </div>
    );
};

export default SortableMenuList;
